/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import MixedChart from 'examples/Charts/MixedChart';
import GradientLineChart from 'examples/Charts/LineCharts/GradientLineChart';
import Calendar from 'examples/Calendar';
import Footer from 'examples/Footer';
import Navbar from 'examples/Navbars/NavBar';
import TimelineItem from 'examples/Timeline/TimelineItem';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import SearchInvite from './SearchInvite';
import SearchExport from './SearchExport';

const initChart = {
  labels: [],
  datasets: [],
};

const initCalendar = {
  labels: { title: '', date: '' },
  initial: Func.dateNow(),
  datasets: [],
};

function DashboardStock() {
  const mod = 'dashboard_stock';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [chartExportStore, setChartExportStore] = useState(initChart);
  const [chartReceive, setChartReceive] = useState(initChart);
  const [chartInvite, setChartInvite] = useState(initChart);
  const [calendarRequest, setCalendarRequest] = useState(initCalendar);
  const [timelineImport, setTimelineImport] = useState([]);
  const [stat, setStat] = useState({
    stat1: { count: '', color: 'white', amount: '', label: '' },
    stat2: { count: '', color: 'white', amount: '', label: '' },
    stat3: { count: '', color: 'white', amount: '', label: '' },
    stat4: { count: '', color: 'white', amount: '', label: '' },
    stat5: { count: '', color: 'white', amount: '', label: '' },
    stat6: { count: '', color: 'white', amount: '', label: '' },
  });

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setStat(result.stat);
        setChartExportStore(result.exportstore);
        setChartReceive(result.receive);
        setChartInvite(result.invite);
        setCalendarRequest(result.request);
        setTimelineImport(result.import);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderStat = () => {
    return (
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                color='dark'
                icon='paid'
                title='รับเข้า'
                count={stat.stat1.count}
                percentage={{
                  color: stat.stat1.color,
                  amount: stat.stat1.amount,
                  label: stat.stat1.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                color='warning'
                icon='weekend'
                title='ขอซื้อ'
                count={stat.stat2.count}
                percentage={{
                  color: stat.stat2.color,
                  amount: stat.stat2.amount,
                  label: stat.stat2.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                icon='leaderboard'
                title='ขอคลัง'
                count={stat.stat3.count}
                percentage={{
                  color: stat.stat3.color,
                  amount: stat.stat3.amount,
                  label: stat.stat3.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                color='success'
                icon='store'
                title='ขอสาขา'
                count={stat.stat4.count}
                percentage={{
                  color: stat.stat4.color,
                  amount: stat.stat4.amount,
                  label: stat.stat4.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                color='primary'
                icon='person_add'
                title='โอนย้าย'
                count={stat.stat5.count}
                percentage={{
                  color: stat.stat5.color,
                  amount: stat.stat5.amount,
                  label: stat.stat5.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5} className='nowrap'>
              <ComplexStatisticsCard
                color='error'
                icon='point_of_sale'
                title='ผลิต'
                count={stat.stat6.count}
                percentage={{
                  color: stat.stat6.color,
                  amount: stat.stat6.amount,
                  label: stat.stat6.label,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderTimeline = () => {
    return (
      <Card sx={{ height: '100%' }}>
        <MDBox pt={3} px={3}>
          <MDTypography variant='h6' fontWeight='medium'>
            รับโอนล่าสุด
          </MDTypography>
          {/* <MDBox mt={0} mb={2}>
            <MDTypography variant="button" color="text" fontWeight="regular">
              <MDTypography display="inline" variant="body2" verticalAlign="middle">
                <Icon sx={{ color: ({ palette: { success } }) => success.main }}>arrow_upward</Icon>
              </MDTypography>
              &nbsp;
              <MDTypography variant="button" color="text" fontWeight="medium">
                24%
              </MDTypography>{" "}
              จากเมื่อวาน
            </MDTypography>
          </MDBox> */}
        </MDBox>
        <MDBox pl={2} mt={2}>
          {timelineImport.map((item, i) => (
            <TimelineItem
              key={item.id}
              color={item.status_color}
              icon={item.status_icon}
              title={`${item.code} : ${item.branch_name}`}
              dateTime={Func.dateTimeDisplay(item.date_record)}
              lastItem={i === timelineImport.length - 1}
            />
          ))}
        </MDBox>
      </Card>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.dashboard`), t(`menu.dashboard_stock`)]} />
      <MDBox py={3}>{renderStat()}</MDBox>
      <MDBox mb={6}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <GradientLineChart
              icon={{ color: 'info', component: 'auto_graph' }}
              title='การโอนสินค้า'
              description={<SearchExport />}
              height='19.75rem'
              chart={chartExportStore}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MixedChart
              icon={{ color: 'primary', component: 'show_chart' }}
              title='การรับเข้า'
              description='จำนวนการรับเข้าต่อวัน'
              height='19.75rem'
              chart={chartReceive}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MixedChart
              icon={{ color: 'success', component: 'insights' }}
              title='การขอสินค้า'
              description={<SearchInvite />}
              height='19.75rem'
              chart={chartInvite}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={8}>
            {useMemo(
              () => (
                <Calendar
                  header={calendarRequest.labels}
                  headerToolbar={false}
                  initialView='dayGridMonth'
                  initialDate={calendarRequest.initial}
                  events={calendarRequest.datasets}
                  selectable
                  editable
                />
              ),
              [calendarRequest.datasets]
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {renderTimeline()}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardStock;
